import { useId, useState, type ReactNode } from "react";
import { ChevronDown, Search } from "lucide-react";
import { cn } from "../cn";
import { Popover } from "./overlay";

export interface MultiSelectOption {
  value: string;
  /** Text shown in the list and in the trigger summary. */
  label: string;
  /** Secondary text under the label in the list. */
  description?: ReactNode;
  disabled?: boolean;
}

export interface MultiSelectProps {
  value: readonly string[];
  onChange: (value: string[]) => void;
  options: readonly MultiSelectOption[];
  /** Accessible name of the popup list (and of the trigger when no `<label>` points at `id`). */
  label: string;
  id?: string;
  placeholder?: string;
  disabled?: boolean;
  /** Shown in the popup when there are no options at all. */
  emptyText?: string;
  /** Placeholder of the search box (shown when there are more than `searchThreshold` options). */
  searchPlaceholder?: string;
  searchThreshold?: number;
  /** Selected values without a matching option are shown as-is (e.g. deleted ids) unless false. */
  keepUnknown?: boolean;
  className?: string;
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
  /** Accepted for `Field` compatibility (validation is up to the caller). */
  required?: boolean;
  "data-testid"?: string;
}

/** Filter options by a case-insensitive substring of label or value. */
export function filterOptions(options: readonly MultiSelectOption[], query: string): MultiSelectOption[] {
  const q = query.trim().toLowerCase();
  if (!q) return [...options];
  return options.filter((o) => o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q));
}

/**
 * Accessible multi-select: a button that summarises the selection and opens a checkbox list in a popover.
 * Works with `Field` (spread the render-prop props onto it: the label targets the button).
 */
export function MultiSelect({
  value,
  onChange,
  options,
  label,
  id,
  placeholder = "—",
  disabled,
  emptyText = "No options",
  searchPlaceholder = "Search",
  searchThreshold = 8,
  keepUnknown = true,
  className,
  "data-testid": testId,
  "aria-invalid": ariaInvalid,
  "aria-describedby": ariaDescribedBy,
}: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const listId = useId();
  const byValue = new Map(options.map((o) => [o.value, o]));
  const selected = value.filter((v) => keepUnknown || byValue.has(v));
  const shown = filterOptions(options, query);
  const toggle = (v: string, on: boolean) =>
    onChange(on ? [...value.filter((x) => x !== v), v] : value.filter((x) => x !== v));
  return (
    <Popover.Root
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setQuery("");
      }}
    >
      <Popover.Trigger asChild disabled={disabled}>
        <button
          type="button"
          id={id}
          data-testid={testId}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-invalid={ariaInvalid}
          aria-describedby={ariaDescribedBy}
          className={cn(
            "flex min-h-9 w-full items-center gap-2 rounded-[var(--radius-control)] border border-line-strong bg-paper px-3 py-1 text-left text-sm text-ink shadow-[var(--shadow-xs)] transition-[border-color,box-shadow]",
            "hover:border-ink-3 focus:border-primary focus:ring-4 focus:ring-primary/15 focus:outline-none",
            "disabled:cursor-not-allowed disabled:bg-subtle disabled:text-ink-3 disabled:shadow-none",
            "aria-[invalid=true]:border-danger",
            className,
          )}
        >
          <span className="flex min-w-0 flex-1 flex-wrap gap-1">
            {selected.length === 0 ? (
              <span className="text-ink-3">{placeholder}</span>
            ) : (
              selected.map((v) => (
                <span
                  key={v}
                  className="inline-flex max-w-full items-center truncate rounded-md bg-info-bg px-1.5 py-0.5 text-xs font-medium text-primary-dark ring-1 ring-primary-dark/20 ring-inset"
                >
                  {byValue.get(v)?.label ?? v}
                </span>
              ))
            )}
          </span>
          <ChevronDown aria-hidden className="size-4 shrink-0 text-ink-2" />
        </button>
      </Popover.Trigger>
      <Popover.Content label={label} className="w-[var(--radix-popover-trigger-width)] min-w-56 p-2">
        {options.length > searchThreshold && (
          <div className="relative mb-2 flex items-center">
            <Search aria-hidden className="pointer-events-none absolute left-2.5 size-4 text-ink-3" />
            <input
              type="search"
              aria-label={searchPlaceholder}
              aria-controls={listId}
              placeholder={searchPlaceholder}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="h-8 w-full rounded-[var(--radius-control)] border border-line-strong bg-paper pr-2 pl-8 text-sm outline-none focus:border-primary focus:ring-4 focus:ring-primary/15"
            />
          </div>
        )}
        <fieldset id={listId}>
          <legend className="sr-only">{label}</legend>
          {options.length === 0 ? (
            <p className="px-2 py-1.5 text-ink-3">{emptyText}</p>
          ) : (
            <ul className="space-y-0.5">
              {shown.map((o) => {
                const optId = `${listId}-${o.value}`;
                return (
                  <li key={o.value}>
                    <label
                      htmlFor={optId}
                      className={cn(
                        "flex cursor-pointer items-start gap-2 rounded-md px-2 py-1.5 hover:bg-hover",
                        o.disabled && "cursor-not-allowed opacity-60",
                      )}
                    >
                      <input
                        id={optId}
                        type="checkbox"
                        className="mt-0.5"
                        checked={value.includes(o.value)}
                        disabled={o.disabled}
                        onChange={(e) => toggle(o.value, e.target.checked)}
                      />
                      <span className="min-w-0">
                        <span className="block">{o.label}</span>
                        {o.description && <span className="block text-xs text-ink-2">{o.description}</span>}
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </fieldset>
      </Popover.Content>
    </Popover.Root>
  );
}
